/*
 * Figma `46:62` — Sidebar Base.
 *
 * It renders the registry and nothing else. Which destinations exist, which phase each sits in and
 * which a role may open are all answered by `navigationFor()`, so the menu cannot show a screen the
 * registry does not know about or hide one it does.
 *
 * At 768–1279px the same markup is an icon rail and at ≤767px the same markup is a drawer — CSS
 * changes the layout, not this component, because three sidebars would be three things to keep in
 * agreement. The one thing the rail needs from here is that every button keeps an accessible name
 * when its label is hidden, which `NavItem` guarantees.
 */
import type { ReactNode } from 'react';
import { NavItem } from '../ui';
import type { ConsoleKind, ConsoleRole, Destination, NavPhase } from '../destinations';

export interface SidebarProps {
  phases: readonly NavPhase[];
  activeKey: string;
  onNavigate: (destination: Destination) => void;
  console: ConsoleKind;
  /** Event name on Event Admin, the product name on SuperAdmin. */
  brandName: string;
  brandTag: string;
  role: ConsoleRole | null;
  roleLabel: string;
  /** Per-destination counts, e.g. slips waiting on `review`. */
  badges?: Readonly<Record<string, number>>;
  icons?: Readonly<Record<string, ReactNode>>;
  onSignOut?: () => void;
  /** Focuses the active item when the drawer opens, so a phone user lands on where they are. */
  activeItemRef?: (el: HTMLButtonElement | null) => void;
}

export function Sidebar({
  phases, activeKey, onNavigate, brandName, brandTag, roleLabel, badges = {}, icons = {}, onSignOut, activeItemRef,
}: SidebarProps) {
  return (
    <aside className="jt-sidebar" aria-label="เมนูหลัก">
      <div className="jt-brand">
        <div className="jt-brand-name" title={brandName}>{brandName}</div>
        <div className="jt-brand-tag">{brandTag}</div>
      </div>

      <nav className="jt-nav">
        {phases.map((phase) => (
          <div className="jt-nav-phase" key={phase.key}>
            {/*
              * A heading over a single destination is the duplication the six-destination site map
              * exists to remove — it would say the same word twice, once as a label and once as a
              * button. Phases holding one item render the button alone.
              */}
            {phase.items.length > 1 && <div className="jt-nav-phase-label">{phase.label}</div>}
            {phase.items.map((destination) => (
              <NavItem
                key={destination.key}
                icon={icons[destination.key]}
                label={destination.navLabel}
                active={destination.key === activeKey}
                badge={badges[destination.key]}
                buttonRef={destination.key === activeKey ? activeItemRef : undefined}
                onClick={() => onNavigate(destination)}
              />
            ))}
          </div>
        ))}
      </nav>

      <div className="jt-sidebar-foot">
        <div className="jt-avatar" aria-hidden="true">{roleLabel.slice(0, 1).toUpperCase()}</div>
        <div className="jt-user-label">{roleLabel}</div>
        {onSignOut && (
          <button type="button" className="jt-btn" data-style="ghost" data-size="sm" data-icon-only="true" aria-label="ออกจากระบบ" onClick={onSignOut}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        )}
      </div>
    </aside>
  );
}
